import type { RoomInfo } from '@/scopes/prices/domain/rooms/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { Room } from '@/scopes/prices/components/RoomSelector/Room'
import { roomGroupSelector, roomTypeFilterOptionsSelector } from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './RoomGroup.module.css'

export function RoomGroup({ roomNumber }: { roomNumber: string }) {
  const intl = useIntl()

  const { rooms, selectedRoom, stock, distribution } = usePricesSelector(roomGroupSelector, roomNumber)
  const { selectRoom } = React.use(PricesActionsContext)

  const setSelectedRoom = (roomId: string | null) => {
    selectRoom(roomNumber, roomId)
  }

  const { selectedValues } = usePricesSelector(roomTypeFilterOptionsSelector)

  if (!rooms || !distribution)
    return null

  const clearSelection = () => {
    setSelectedRoom(null)
  }

  const selectedRoomInfo = rooms.find(room => room.id === selectedRoom)

  const buildDistributionText = () => {
    let text = `${intl.formatMessage(
      { id: 'prices_room_selector_room_group-distribution_adults' },
      { adults: distribution.adults },
    )}`
    if (distribution.children > 0) {
      text += `${intl.formatMessage(
        { id: 'prices_room_selector_room_group-distribution_children' },
        { children: distribution.children },
      )}`
    }
    if (distribution.babies > 0) {
      text += `${intl.formatMessage(
        { id: 'prices_room_selector_room_group-distribution_babies' },
        { babies: distribution.babies },
      )}`
    }
    return text
  }

  const filteredRooms: RoomInfo[] = []
  const otherRooms: RoomInfo[] = []

  if (!isEmpty(selectedValues)) {
    const selectedRooms = selectedValues as string[]
    rooms.forEach((room) => {
      if (stock[room.id] !== 0) {
        if (selectedRooms.includes(room.id)) {
          filteredRooms.push(room)
        }
        else {
          otherRooms.push(room)
        }
      }
    })
  }

  return (
    <div className={styles.roomGroup} data-qa-room-selector={roomNumber}>
      <div className={styles.header}>
        <Typography className={styles.title} variant="body-l-bold-snug">
          {intl.formatMessage({ id: 'prices_room_selector_room_group-title' }, { roomNumber })}
        </Typography>
        <Typography className={styles.distribution} variant="body-m-regular-snug">{buildDistributionText()}</Typography>
        {selectedRoom && (
          <Button className={styles.clearSelection} variant="transparentPrimary" onClick={clearSelection}>
            {intl.formatMessage({ id: 'prices_room_selector_room_group-clear-selection' })}
          </Button>
        )}
      </div>
      {selectedRoom && selectedRoomInfo
        ? (
            <RadioButtonGroup value={selectedRoom}>
              <Room roomNumber={roomNumber} {...selectedRoomInfo} />
            </RadioButtonGroup>
          )
        : (
            <RadioButtonGroup value={selectedRoom as string}>
              {filteredRooms.length > 0
                ? (
                    <>
                      <div className={styles.rooms}>
                        <Typography variant="body-m-regular-snug">
                          {intl.formatMessage({ id: 'prices_room_selector_filtered_rooms' })}
                        </Typography>
                        <div className={styles.rooms}>
                          {filteredRooms.map(
                            room => stock[room.id] !== 0 && <Room roomNumber={roomNumber} key={room.id} id={room.id} />,
                          )}
                        </div>
                      </div>
                      <hr className={styles.divider} />
                      <div className={styles.rooms}>
                        <Typography variant="body-m-regular-snug">
                          {intl.formatMessage({ id: 'prices_room_selector_other_rooms' })}
                        </Typography>
                        <div className={styles.rooms}>
                          {otherRooms.map(
                            room => stock[room.id] !== 0 && <Room roomNumber={roomNumber} key={room.id} id={room.id} />,
                          )}
                        </div>
                      </div>
                    </>
                  )
                : (
                    <div className={styles.rooms}>
                      {rooms.map(room => stock[room.id] !== 0
                        && <Room roomNumber={roomNumber} key={room.id} id={room.id} />)}
                    </div>
                  )}
            </RadioButtonGroup>
          )}
    </div>
  )
}
